import React, { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import moment from 'moment';
import {
  Stack,
  Card,
  Icon,
  Text,
  Button,
  Tag,
  withTranslation,
  Spacer,
  formatDate,
} from '@piwikpro/ui-components';
import { AuditLogState } from '@piwikpro/audit-log-crate/reducer';
import { OrganizationState } from '@piwikpro/organization-crate';
import EditAuditLogRetentionModal from './EditAuditLogRetentionModal';

const UNLIMITED_ANALYTICS_RETENTION_LIMIT = -1;

const mapStateToProps = (state: {
  organization: OrganizationState
  auditLog: AuditLogState
}) => ({
  analyticsDataRetention: state.organization.details.dataRetention!,
  analyticsDataRetentionLimit: state.organization.details.license.current?.plan.administration.data_retention_limit ?? null,
  auditLogDataRetention: state.auditLog.retention.details,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface Props extends ReduxProps {
  t: TFunction
}

export const LicenseDetailsDataRetention: React.FC<Props> = ({
  analyticsDataRetention,
  analyticsDataRetentionLimit,
  auditLogDataRetention,
  t,
}) => {
  const [auditLogRetentionEditVisible, setAuditLogRetentionEditVisible] = useState(false);
  const analyticsRetentionPeriod = analyticsDataRetentionLimit === UNLIMITED_ANALYTICS_RETENTION_LIMIT
    ? null
    : analyticsDataRetentionLimit;

  if (
    analyticsRetentionPeriod === null
    && auditLogDataRetention!.currentPeriod === null
  ) {
    return null;
  }
  const analyticsRetentionTurnedOn = analyticsRetentionPeriod !== null;
  const auditLogRetentionTurnedOn = auditLogDataRetention!.currentPeriod !== null;
  const analyticsStartDate = analyticsDataRetention.start_date || moment();
  const auditLogOldestEventDate = auditLogDataRetention!.oldestEvent;
  const auditLogNoEvents = auditLogOldestEventDate === null;
  const analyticsNoEvents = analyticsDataRetention.start_date === null;
  const auditLogRetentionPeriod = auditLogDataRetention!.currentPeriod;
  const analyticsRetentionReachedDate = analyticsRetentionPeriod !== null
    ? moment(analyticsStartDate).startOf('day').add(analyticsRetentionPeriod, 'months')
    : null;
  const auditLogRetentionReachedDate = moment(auditLogOldestEventDate).startOf('day').add(auditLogRetentionPeriod, 'months');
  const hasAnalyticsRetentionPeriodReached = analyticsRetentionReachedDate
    && moment().isSameOrAfter(moment(analyticsRetentionReachedDate).startOf('day'), 'days');
  const hasAuditLogRetentionPeriodReached = auditLogRetentionReachedDate
    && moment().isSameOrAfter(moment(auditLogRetentionReachedDate).startOf('day'), 'days');

  return (
    <>
      {auditLogRetentionEditVisible && (
        <EditAuditLogRetentionModal
          onClose={() => setAuditLogRetentionEditVisible(false)}
        />
      )}
      <Stack spacing="narrow" vertical>
        <Stack.Item previousSpacing="default">
          <Text as="p" weight={600} dataId="Label">
            {t('platform.license.data-retention.title')}
          </Text>
        </Stack.Item>
        <Stack.Item fill>
          <Card>
            {analyticsRetentionTurnedOn && (
              <Card.Section padding="narrow">
                <Stack verticalAlignment="center">
                  <Stack.Item fill>
                    <Stack verticalAlignment="center" spacing="narrow">
                      <Stack.Item>
                        <Text as="p">{t('analytics')}</Text>
                      </Stack.Item>
                      <Stack.Item>
                        <Tag>{t('platform.license.summary.row.data-retention.months-value', { count: analyticsRetentionPeriod! })}</Tag>
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item>
                    <Stack verticalAlignment="center" spacing="narrow">
                      <Stack.Item>
                        <Icon name="info-circle" />
                      </Stack.Item>
                      <Stack.Item>
                        <Text as="p" size={12} color="InkLighter" dataId="Caption">
                          {analyticsNoEvents ? t('platform.license.data-retention.no-data-collected-yet.info') : (
                            t(hasAnalyticsRetentionPeriodReached ? 'platform.license.data-retention.period-reached.info' : 'platform.license.data-retention.period-not-reached.info-new',
                              {
                                date: hasAnalyticsRetentionPeriodReached
                                  ? formatDate(moment().subtract(analyticsRetentionPeriod!, 'months').toDate(), { format: 'LL' })
                                  : formatDate(moment(analyticsStartDate).add(analyticsRetentionPeriod!, 'months').toDate(), { format: 'LL' }),
                                retentionPeriod: analyticsRetentionPeriod!,
                              })
                          )}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                </Stack>
              </Card.Section>
            )}
            {auditLogRetentionTurnedOn && (
              <Card.Section dataId="AuditLogRetention" padding="narrow">
                <Stack verticalAlignment="center">
                  <Stack.Item fill>
                    <Stack verticalAlignment="center" spacing="narrow">
                      <Stack.Item>
                        <Text as="p">{t('platform.auditLog.title')}</Text>
                      </Stack.Item>
                      <Stack.Item>
                        <Tag>{t('platform.license.summary.row.data-retention.months-value', { count: auditLogRetentionPeriod })}</Tag>
                      </Stack.Item>
                      <Stack.Item previousSpacing="extraNarrow">
                        <Button
                          onClick={() => setAuditLogRetentionEditVisible(true)}
                          appearance="simple"
                          size="small"
                          icon="edit"
                          ariaLabel={t('platform.license.summary.row.data-retention.edit-audit-log-retention')}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item>
                    <Stack verticalAlignment="center" spacing="narrow">
                      <Stack.Item>
                        <Icon name="info-circle" />
                      </Stack.Item>
                      <Stack.Item>
                        <Text as="p" size={12} color="InkLighter" dataId="Caption">
                          {auditLogNoEvents ? t('platform.license.data-retention.no-data-collected-yet.info') : (
                            t(hasAuditLogRetentionPeriodReached ? 'platform.license.data-retention.period-reached.info' : 'platform.license.data-retention.period-not-reached.info-new',
                              {
                                date: hasAuditLogRetentionPeriodReached
                                  ? formatDate(moment().subtract(auditLogRetentionPeriod, 'months').toDate(), { format: 'LL' })
                                  : formatDate(moment(auditLogOldestEventDate).add(auditLogRetentionPeriod, 'months').toDate(), { format: 'LL' }),
                                retentionPeriod: auditLogRetentionPeriod,
                              })
                          )}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                </Stack>
              </Card.Section>
            )}
          </Card>
        </Stack.Item>
        <Spacer size={0} />
      </Stack>
    </>
  );
};

export default connector(withTranslation(['administration'])(LicenseDetailsDataRetention as any));
